import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/data";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const dark = !scrolled; // hero is light-on-navy at top? We use light bg site → keep navy text always

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 py-3 shadow-[0_10px_30px_-20px_rgba(11,27,61,0.25)]"
          : "bg-transparent py-6"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="font-serif text-2xl tracking-tight text-navy">Epic</span>
          <span className="text-[0.65rem] tracking-[0.28em] uppercase text-gold font-semibold pb-0.5">
            Consultancy
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              className={`text-sm font-medium link-underline transition-colors duration-300 ${
                location.pathname === l.to ? "text-gold" : "text-navy/80 hover:text-navy"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button asChild className="rounded-full bg-navy hover:bg-navy-light text-cream px-6">
            <Link to="/contact" data-testid="nav-book-cta">Book a Consultation</Link>
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-navy"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 mt-3">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-lg font-medium text-navy"
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              >
                {l.label}
              </Link>
            ))}
            <Button asChild className="rounded-full bg-navy text-cream mt-2">
              <Link to="/contact">Book a Consultation</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
